import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "../../../components/base/Button";
import { getData, postData } from "../../../services/FetchNodeServices";
import { toast } from "react-toastify";

// ─── SearchableItemInput ──────────────────────────────────────────────────────

function SearchableItemInput({ value, options = [], onChange, onSelect, placeholder = "Search item...", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const filtered = value?.trim()
    ? options.filter((o) =>
      o.name?.toLowerCase().includes(value.toLowerCase()) ||
      (o.code ?? "").toLowerCase().includes(value.toLowerCase()),
    )
    : options;

  useEffect(() => { setHighlight(-1); }, [value]);

  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    listRef.current.children[highlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = (opt) => { onSelect(opt); setOpen(false); setHighlight(-1); };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); setOpen(true); }
      return;
    }
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); setHighlight((i) => Math.min(i + 1, filtered.length - 1)); break;
      case "ArrowUp": e.preventDefault(); setHighlight((i) => Math.max(i - 1, 0)); break;
      case "Enter":
        e.preventDefault();
        if (highlightIdx >= 0 && filtered[highlightIdx]) handleSelect(filtered[highlightIdx]);
        break;
      case "Tab":
        if (filtered[0]) handleSelect(filtered[0]);
        setOpen(false);
        break;
      case "Escape": setOpen(false); break;
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="relative">
        <input
          type="text" value={value || ""} disabled={disabled}
          placeholder={placeholder} autoComplete="off"
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <i className={`ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && !disabled && (
        <div ref={listRef}
          className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-y-auto"
          style={{ maxHeight: 240, minWidth: 340 }}
        >
          <div className="grid grid-cols-4 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50 sticky top-0">
            <span>Item</span>
            <span className="text-center">Code</span>
            <span className="text-center">Stock</span>
            <span className="text-right">Rate</span>
          </div>

          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-xs text-center text-gray-400">
              {value ? `No items matching "${value}"` : "No items available"}
            </div>
          ) : filtered.map((opt, idx) => (
            <div key={opt.id || opt.name || idx}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
              onMouseEnter={() => setHighlight(idx)}
              className={`grid grid-cols-4 items-center px-3 py-2 cursor-pointer text-sm transition-colors ${idx === highlightIdx ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
            >
              <span className={`font-medium truncate ${idx === highlightIdx ? "text-blue-600" : "text-gray-800"}`}>{opt.name}</span>
              <span className="text-center text-xs text-gray-400 font-mono">{opt.code || "—"}</span>
              <span className={`text-center text-xs font-medium ${(opt.stock ?? 0) < 10 ? "text-red-500" : "text-gray-500"}`}>
                {opt.stock ?? 0}
              </span>
              <span className={`text-right text-xs font-medium ${idx === highlightIdx ? "text-blue-600" : "text-gray-700"}`}>
                {(opt.rate ?? 0) > 0 ? `₹${Number(opt.rate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SearchableCustomerDropdown ───────────────────────────────────────────────

function SearchableCustomerDropdown({
  selectedCustomer, setSelectedCustomer,
  customers, customerLoading, customerSearch, setCustomerSearch,
  customerPage, customerTotalPages, fetchCustomers,
  handleCustomerChange, setReturnForm, setSelectedCustomerOrders,
}) {
  const [open, setOpen] = useState(false);

  const handleClear = () => {
    setSelectedCustomer(null);
    setReturnForm((prev) => ({ ...prev, customerId: "", orderId: "", items: [] }));
    setSelectedCustomerOrders([]);
    setCustomerSearch("");
    fetchCustomers(1, "");
  };

  const handlePick = (c) => {
    setSelectedCustomer(c);
    handleCustomerChange(c._id, "return");
    setOpen(false);
    setCustomerSearch("");
  };

  if (selectedCustomer) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
        <div className="w-full px-3 py-2 border border-orange-400 rounded-lg text-sm bg-orange-50 flex justify-between items-center">
          <div className="flex flex-col">
            <span className="font-medium text-orange-800">{selectedCustomer.name}</span>
            <span className="text-xs text-orange-500">{selectedCustomer.email} • {selectedCustomer.phone}</span>
          </div>
          <button onClick={handleClear} className="ml-2 text-orange-400 hover:text-red-500 shrink-0">
            <i className="ri-close-circle-line text-base" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
      <div
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer flex justify-between items-center bg-white"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-gray-400">Choose Customer</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-gray-500`} />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input
              autoFocus type="text" value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search by name, email, phone..."
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {customerLoading ? (
              <div className="text-center py-4 text-sm text-gray-500">
                <div className="inline-block w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-1" />
                Loading...
              </div>
            ) : customers?.length === 0 ? (
              <div className="text-center py-4 text-sm text-gray-400">No customers found</div>
            ) : customers.map((c) => (
              <div key={c._id} onClick={() => handlePick(c)}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 flex flex-col">
                <span className="text-gray-800 font-medium">{c.name}</span>
                <span className="text-gray-400 text-xs">{c.email} • {c.phone}</span>
              </div>
            ))}
          </div>
          {customerTotalPages > 1 && (
            <div className="flex justify-between items-center px-3 py-2 border-t text-xs text-gray-500">
              <button disabled={customerPage <= 1}
                onClick={() => fetchCustomers(customerPage - 1, customerSearch)}
                className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40">
                ← Prev
              </button>
              <span>Page {customerPage} of {customerTotalPages}</span>
              <button disabled={customerPage >= customerTotalPages}
                onClick={() => fetchCustomers(customerPage + 1, customerSearch)}
                className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40">
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * deliveredPcs = quantity (sets) × pcsInSet
 * From order data: { quantity: 2, pcsInSet: 6 } → deliveredPcs: 12
 * The server already computes this and sends deliveredPcs directly,
 * but we also derive it as a fallback.
 */
const resolveDeliveredPcs = (item) => {
  if (item.deliveredPcs != null && item.deliveredPcs > 0) return item.deliveredPcs;
  const qty = Number(item.quantity ?? item.dispatchedQty ?? 0);
  const pcsInSet = Number(item.pcsInSet ?? 1);
  return qty * pcsInSet;
};

/**
 * Map a raw order item → normalised return-form item.
 * ✅ returnPcs always starts at 0 (never pre-filled from server).
 */
const mapOrderItemToFormItem = (item) => ({
  // identity
  productId: item.productId,
  color: item.color ?? "",
  availableSizes: item.availableSizes ?? [],
  selectedSizes: item.selectedSizes ?? [],
  images: item.images ?? [],
  pcsInSet: Number(item.pcsInSet ?? 1),
  singlePicPrice: Number(item.singlePicPrice ?? item.price ?? 0),

  // quantities
  deliveredPcs: resolveDeliveredPcs(item),  // ✅ correct quantity * pcsInSet
  alreadyReturned: 0,
  returnPcs: 0,          // ✅ ALWAYS start at 0 — never carry server value
  refundAmount: 0,

  reason: "",
});

// ─── CreateReturnModal ────────────────────────────────────────────────────────

function CreateReturnModal({
  setShowCreateReturnModal,
  setReturnForm,
  setSelectedCustomerOrders,
  customers,
  returnForm,
  selectedCustomerOrders,
  refundMethods,
  handleCustomerChange,
  handleOrderChange,
  fetchReturn,
  returns,
  customerSearch,
  setCustomerSearch,
  customerLoading,
  customerPage,
  customerTotalPages,
  fetchCustomers,
}) {
  const [previewOrder, setPreviewOrder] = useState([]);
  const [returnMethod, setReturnMethod] = useState("With Orders");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Item options
  const [itemOptions, setItemOptions] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemPage, setItemPage] = useState(1);
  const [itemTotalPages, setItemTotalPages] = useState(1);
  const [loadingItems, setLoadingItems] = useState(false);

  // Debounce item search
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setItemPage(1);
      fetchItems(1, itemSearch);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [itemSearch]);

  useEffect(() => { fetchItems(itemPage, itemSearch); }, [itemPage]); // eslint-disable-line

  // ── Fetch item catalogue ────────────────────────────────────────────────────
  const fetchItems = useCallback(async (page = 1, search = "") => {
    setLoadingItems(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search.trim()) params.append("search", search.trim());
      const res = await getData(`api/subProduct/get-all-sub-products-with-pagination?${params}`);
      if (res?.success) {
        const mapped = (res.data || []).map((p) => {
          const pcsInSet = Number(p.pcsInSet ?? 1);
          const lotStock = Number(p.lotStock ?? 0);
          return {
            id: p._id || p.id || "",
            name: p.name || p.color || p.title || "",
            code: p.code || p.lotNumber || "",
            // ✅ FIX: correct precedence — Math.floor(lotStock / pcsInSet)
            stock: pcsInSet > 0 ? Math.floor(lotStock / pcsInSet) : 0,
            rate: Number(p.singlePicPrice ?? p.price ?? p.rate ?? 0),
            unit: p.unit || "Pcs",
            pcsInSet,
          };
        });
        setItemOptions(mapped);
        setItemTotalPages(res?.pagination?.totalPages || 1);
      } else {
        toast.error(res?.message || "Failed to fetch items");
      }
    } catch {
      toast.error("Network error — could not load items");
    } finally {
      setLoadingItems(false);
    }
  }, []);

  // ── Reset ───────────────────────────────────────────────────────────────────
  const resetReturnForm = () => {
    setReturnForm({
      customerId: "",
      orderId: "",
      items: [],
      reason: "",
      refundMethod: refundMethods?.[0] || "Original Payment Method",
    });
    setSelectedCustomerOrders([]);
    setCustomerSearch("");
    setSelectedCustomer(null);
  };

  // ── Update a single field on a return item row ──────────────────────────────
  const updateReturnItem = (index, field, value, alreadyReturned) => {
    setReturnForm((prev) => {
      const items = prev.items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };

        if (field === "returnPcs") {
          // ✅ Cap at (deliveredPcs - alreadyReturned), never go negative
          const maxAllowed = Math.max((item.deliveredPcs ?? 0) - (item.alreadyReturned ?? 0), 0);
          const qty = Math.min(Math.max(0, Number(value)), maxAllowed);
          const price = Number(item.singlePicPrice || item.price || 0);
          updated.returnPcs = qty;
          updated.refundAmount = qty * price;
          updated.alreadyReturned = alreadyReturned ?? item.alreadyReturned ?? 0;
        }
        return updated;
      });
      return { ...prev, items };
    });
  };

  // ── Fetch already-returned counts for this order ────────────────────────────
  const fetchReturnByCustomerAndOrder = useCallback(async () => {
    if (!returnForm?.customerId || !returnForm?.orderId) return;
    try {
      const res = await postData("api/return/get-all-returns-by-customer-and-order", {
        customerId: returnForm.customerId,
        orderId: returnForm.orderId,
      });
      if (res?.status) setPreviewOrder(res.data);
    } catch {
      // silent — non-critical
    }
  }, [returnForm?.customerId, returnForm?.orderId]);

  useEffect(() => { fetchReturnByCustomerAndOrder(); }, [fetchReturnByCustomerAndOrder]);

  // ── Merge order items with already-returned counts ──────────────────────────
  const mergedItems = useMemo(() => {
    if (!returnForm?.items?.length) return [];
    return returnForm.items.map((item) => {
      const returnCount = previewOrder
        ?.flatMap((ch) => ch.items)
        ?.filter((i) => i?.color === item?.color)
        ?.reduce((sum, i) => sum + (i?.returnPcs || 0), 0) ?? 0;

      const deliveredPcs = resolveDeliveredPcs(item);
      const remaining = Math.max(deliveredPcs - returnCount, 0);

      return {
        ...item,
        deliveredPcs,           // ✅ always correct
        alreadyReturned: returnCount,
        remainingQty: remaining,
        returnPcs: item.returnPcs ?? 0,  // ✅ never use server's returnPcs
      };
    });
  }, [returnForm?.items, previewOrder]);

  const deliveredOrders = selectedCustomerOrders.filter((o) => o?.status === "Delivered");

  const totalRefund = mergedItems.reduce(
    (sum, item) =>
      sum + Number(item.returnPcs || 0) * Number(item.singlePicPrice || item.price || 0),
    0,
  );

  // isAllSelected: every item's returnPcs equals its remainingQty (from mergedItems, which has correct alreadyReturned)
  const isAllSelected = mergedItems.length > 0 && mergedItems.every(
    (item) => Number(item.returnPcs ?? 0) === Number(item.remainingQty ?? 0)
  );

  const handleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all — clear every row
      setReturnForm((prev) => ({
        ...prev,
        items: prev.items.map((item) => ({ ...item, returnPcs: 0, refundAmount: 0 })),
      }));
    } else {
      // Select all — use remainingQty from mergedItems (correct alreadyReturned baked in)
      setReturnForm((prev) => ({
        ...prev,
        items: prev.items.map((item, index) => {
          const remaining = Number(mergedItems[index]?.remainingQty ?? 0);
          const price = Number(item.singlePicPrice || item.price || 0);
          return { ...item, returnPcs: remaining, refundAmount: remaining * price };
        }),
      }));
    }
  };

  // ── Create return ──────────────────────────────────────────────────────────
  const createReturn = async () => {
    const customer = selectedCustomer || customers?.find((c) => c?._id === returnForm?.customerId);
    const itemsToReturn = returnForm?.items?.filter((item) => (item?.returnPcs ?? 0) > 0);

    if (!customer || !itemsToReturn?.length) {
      toast.warning("Please select a customer and set at least one return quantity.");
      return;
    }

    const totalRefundAmt = itemsToReturn.reduce((sum, item) => sum + (item?.refundAmount || 0), 0);
    const returnNumber = `RET-${new Date().getFullYear()}-${String(returns.length + 1).padStart(3, "0")}`;

    const newReturn = {
      returnNumber,
      customer: customer.name,
      customerId: customer._id,
      orderNumber: returnForm?.orderNumber,
      orderId: returnForm?.orderId,
      items: itemsToReturn.map((item) => ({
        color: item.color,
        availableSizes: item.availableSizes,
        returnPcs: item.returnPcs,
        lotNumber: item.productId?.lotNumber,
        reason: item.reason,
        refundAmount: item.refundAmount,
        pcsInSet: item.pcsInSet,
        productId: item.productId?._id,
        alreadyReturned: item.alreadyReturned,
        singlePicPrice: item.singlePicPrice,
      })),
      totalRefund: totalRefundAmt,
      date: new Date().toISOString().split("T")[0],
      status: "Pending",
      refundMethod: returnForm?.refundMethod,
    };

    try {
      const response = await postData("api/return/create-return", { data: newReturn });
      if (response?.success) {
        await fetchReturn();
        resetReturnForm();
        setShowCreateReturnModal(false);
        toast.success("Return created! Stock will update when approved.");
      } else {
        toast.error("Failed to create return. Please try again.");
      }
    } catch {
      toast.error("An error occurred while creating the return.");
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Create Return</h2>
            <div className="flex items-center gap-3">
              <select value={returnMethod} onChange={(e) => setReturnMethod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
                {["With Orders", "Without Orders"].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <button
                onClick={() => { setShowCreateReturnModal(false); resetReturnForm(); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>

          {/* ── WITHOUT ORDERS ── */}
          {returnMethod === "Without Orders" ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <SearchableCustomerDropdown
                  selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
                  customers={customers} customerLoading={customerLoading}
                  customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
                  customerPage={customerPage} customerTotalPages={customerTotalPages}
                  fetchCustomers={fetchCustomers} handleCustomerChange={handleCustomerChange}
                  setReturnForm={setReturnForm} setSelectedCustomerOrders={setSelectedCustomerOrders}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-gray-800">Return Items</h3>
                  <button type="button"
                    onClick={() => setReturnForm((prev) => ({
                      ...prev,
                      items: [...(prev.items || []), {
                        color: "", lotNumber: "", deliveredPcs: 0,
                        alreadyReturned: 0, returnPcs: 0, reason: "", refundAmount: 0,
                      }],
                    }))}
                    className="bg-blue-900 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1">
                    <i className="ri-add-line" /> Add Item
                  </button>
                </div>

                {returnForm?.items?.length > 0 ? (
                  <div className="space-y-3">
                    {returnForm.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100 relative">
                        <button type="button"
                          onClick={() => setReturnForm((prev) => ({
                            ...prev,
                            items: prev.items.filter((_, i) => i !== index),
                          }))}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-600">
                          <i className="ri-close-line text-lg" />
                        </button>

                        <div className="grid grid-cols-6 gap-3 items-end">
                          {/* Item Name */}
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Item Name *</label>
                            <SearchableItemInput
                              value={item.color || ""}
                              options={itemOptions}
                              onChange={(name) => updateReturnItem(index, "color", name)}
                              onSelect={(opt) => {
                                // ✅ Auto-fill deliveredPcs from stock (sets available)
                                const pcsInSet = Number(opt.pcsInSet ?? 1);
                                const deliveredPcs = opt.stock ?? 0; // stock already = lotStock / pcsInSet

                                updateReturnItem(index, "color", opt.name);
                                updateReturnItem(index, "item_id", opt.id);
                                updateReturnItem(index, "pcsInSet", pcsInSet);
                                updateReturnItem(index, "singlePicPrice", opt.rate ?? 0);
                                updateReturnItem(index, "rate", opt.rate ?? 0);
                                updateReturnItem(index, "deliveredPcs", deliveredPcs);
                                // ✅ returnPcs always reset to 0 on selection
                                updateReturnItem(index, "returnPcs", 0);
                                updateReturnItem(index, "refundAmount", 0);
                              }}
                            />
                          </div>

                          {/* Delivered PCS */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Delivered Pcs</label>
                            <input type="number" min="0" value={item.deliveredPcs || 0}
                              onChange={(e) => updateReturnItem(index, "deliveredPcs", parseInt(e.target.value) || 0)}
                              className={`${inputCls} text-center`} />
                          </div>

                          {/* Return Qty */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Return Qty
                              {item.deliveredPcs > 0 && (
                                <span className="ml-1 text-gray-400 font-normal">
                                  (max {Math.max((item.deliveredPcs || 0) - (item.alreadyReturned || 0), 0)})
                                </span>
                              )}
                            </label>
                            <input type="number" min="0"
                              max={Math.max((item.deliveredPcs || 0) - (item.alreadyReturned || 0), 0)}
                              value={item.returnPcs || 0}
                              onChange={(e) => updateReturnItem(index, "returnPcs", parseInt(e.target.value) || 0)}
                              className={`${inputCls} text-center`} />
                          </div>

                          {/* Reason */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
                            <input type="text" value={item.reason || ""}
                              onChange={(e) => updateReturnItem(index, "reason", e.target.value)}
                              placeholder="Reason"
                              className={inputCls} />
                          </div>

                          {/* Refund */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Refund (₹)</label>
                            <input type="number" min="0" value={item.refundAmount || 0}
                              onChange={(e) => updateReturnItem(index, "refundAmount", parseFloat(e.target.value) || 0)}
                              className={`${inputCls} text-center`} />
                          </div>
                        </div>

                        {/* Summary pill */}
                        {item.pcsInSet > 0 && item.deliveredPcs > 0 && (
                          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                            <span>Sets: <strong className="text-gray-600">{Math.round(item.deliveredPcs / item.pcsInSet)}</strong></span>
                            <span>Pcs/Set: <strong className="text-gray-600">{item.pcsInSet}</strong></span>
                            <span>Total Pcs: <strong className="text-gray-600">{item.deliveredPcs}</strong></span>
                            {item.singlePicPrice > 0 && (
                              <span>Price/Pc: <strong className="text-gray-600">₹{item.singlePicPrice}</strong></span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic mt-2">
                    No items added yet. Click "+ Add Item" to start.
                  </p>
                )}
              </div>

              {/* Total */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Total Refund:</span>
                  <span className="text-xl font-bold text-orange-600">
                    ₹{returnForm.items?.reduce((sum, i) => sum + (i.refundAmount || 0), 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method</label>
                <select value={returnForm?.refundMethod}
                  onChange={(e) => setReturnForm((prev) => ({ ...prev, refundMethod: e.target.value }))}
                  className={inputCls}>
                  {refundMethods?.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="flex gap-3">
                <Button className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  onClick={() => { setShowCreateReturnModal(false); resetReturnForm(); }}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-green-600 text-white hover:bg-green-700" onClick={createReturn}>
                  Create Return
                </Button>
              </div>
            </div>

          ) : (
            /* ── WITH ORDERS ── */
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <SearchableCustomerDropdown
                  selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
                  customers={customers} customerLoading={customerLoading}
                  customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
                  customerPage={customerPage} customerTotalPages={customerTotalPages}
                  fetchCustomers={fetchCustomers} handleCustomerChange={handleCustomerChange}
                  setReturnForm={setReturnForm} setSelectedCustomerOrders={setSelectedCustomerOrders}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Order</label>
                  <select value={returnForm?.orderId}
                    onChange={(e) => handleOrderChange(e.target.value, "return")}
                    disabled={!returnForm?.customerId}
                    className={`${inputCls} disabled:bg-gray-50 disabled:cursor-not-allowed`}>
                    <option value="">Choose Order</option>
                    {deliveredOrders.map((order) => (
                      <option key={order._id} value={order._id}>
                        {order.orderNumber} — ₹{order.subtotal?.toLocaleString("en-IN")} ({order.status})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {mergedItems.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-800">Return Items</h3>
                    <Button onClick={handleSelectAll} className="text-sm">{isAllSelected ? 'Deselect All' : 'Select All'}</Button>
                  </div>
                  <div className="space-y-3">
                    {mergedItems.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <div className="grid grid-cols-7 gap-3 items-end">

                          {/* Item / Lot */}
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Item / Lot</label>
                            <div className="font-medium text-gray-800 text-sm">
                              {item.productId?.lotNumber ? ` / ${item.productId.lotNumber}` : ""}
                              {item.color}
                            </div>
                            {/* ✅ Show sets × pcsInSet breakdown */}
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {item.pcsInSet > 0
                                ? `${Math.round(item.deliveredPcs / item.pcsInSet)} sets × ${item.pcsInSet} pcs`
                                : `${item.deliveredPcs} pcs`}
                            </div>
                          </div>

                          {/* Dispatched */}
                          <div className="text-center">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Dispatched Pcs</label>
                            <div className="font-semibold text-gray-700">{item.deliveredPcs ?? 0}</div>
                          </div>

                          {/* Already returned */}
                          <div className="text-center">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Returned</label>
                            <div className="font-semibold text-green-600">{item.alreadyReturned ?? 0}</div>
                          </div>

                          {/* Remaining */}
                          <div className="text-center">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Remaining</label>
                            <div className={`font-semibold ${item.remainingQty === 0 ? "text-gray-400" : "text-blue-600"}`}>
                              {item.remainingQty ?? 0}
                            </div>
                          </div>

                          {/* Return Qty */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Return Qty
                            </label>
                            <input type="number" min="0"
                              max={item.remainingQty ?? 0}
                              value={item.returnPcs || 0}
                              disabled={item.remainingQty === 0}
                              onChange={(e) => updateReturnItem(
                                index, "returnPcs",
                                parseInt(e.target.value) || 0,
                                item.alreadyReturned,
                              )}
                              className={`${inputCls} text-center disabled:bg-gray-100 disabled:cursor-not-allowed`}
                            />
                          </div>

                          {/* Reason */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
                            <input type="text" value={item.reason || ""}
                              onChange={(e) => updateReturnItem(index, "reason", e.target.value)}
                              placeholder="Reason"
                              className={inputCls} />
                          </div>
                        </div>

                        {/* Refund row */}
                        <div className="mt-2 flex justify-between items-center">
                          <div className="text-xs text-gray-400">
                            ₹{item.singlePicPrice} × {item.returnPcs || 0} pcs
                          </div>
                          <div className="font-semibold text-orange-600 text-sm">
                            Refund: ₹{(item.refundAmount ?? 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Total Refund:</span>
                      <span className="text-xl font-bold text-orange-600">
                        ₹{totalRefund.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method</label>
                <select value={returnForm?.refundMethod}
                  onChange={(e) => setReturnForm((prev) => ({ ...prev, refundMethod: e.target.value }))}
                  className={inputCls}>
                  {refundMethods?.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  onClick={() => { setShowCreateReturnModal(false); resetReturnForm(); }}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-orange-600 text-white hover:bg-orange-700"
                  disabled={
                    !returnForm.customerId ||
                    !returnForm.orderId ||
                    mergedItems.every((i) => !i.returnPcs)
                  }
                  onClick={createReturn}>
                  Create Return
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateReturnModal;